import { useCallback, useEffect, useState } from 'react';
import type { Api, WaView } from '../api';
import type { T } from '../i18n';
import { DangerButton, Field } from '../ui';
import { errorText } from './AgentSettings';

type Msg = { kind: 'ok' | 'error'; text: string };

/** ADR-069: the workspace's own WhatsApp (Cloud API) account, its templates, a test message and the log. */
export const WhatsAppAccountCard = ({ api, t }: { api: Api; t: T }) => {
  const [view, setView] = useState<WaView>();
  const [editing, setEditing] = useState(false);
  const [x, setX] = useState({ phoneNumberId: '', wabaId: '', token: '', defaultLanguage: '' });
  const [test, setTest] = useState({ to: '', template: '', params: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>();

  const load = useCallback(() => {
    api.whatsapp().then(setView).catch((e: unknown) => setMsg({ kind: 'error', text: errorText(t, e) }));
  }, [api, t]);
  useEffect(load, [load]);

  const run = async (f: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(undefined);
    try {
      await f();
      setMsg({ kind: 'ok', text: ok });
      load();
      return true;
    } catch (e) {
      setMsg({ kind: 'error', text: errorText(t, e) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const account = view?.account;
  const configured = account?.configured === true;
  const hosted = account?.managedBy === 'host';
  const startEdit = () => {
    setX(configured && account.managedBy !== 'host'
      ? { phoneNumberId: account.phoneNumberId, wabaId: account.wabaId, token: '', defaultLanguage: account.defaultLanguage ?? '' }
      : { phoneNumberId: '', wabaId: '', token: '', defaultLanguage: '' });
    setEditing(true);
  };
  const save = () =>
    void run(
      () => api.saveWhatsApp({ phoneNumberId: x.phoneNumberId.trim(), wabaId: x.wabaId.trim(), defaultLanguage: x.defaultLanguage.trim() || null, ...(x.token.trim() ? { token: x.token.trim() } : {}) }),
      t('wa.saved'),
    ).then((ok) => ok && setEditing(false));
  const approved = configured ? (account.templates ?? []).filter((tp) => tp.status === 'APPROVED') : [];
  const chosen = approved.find((tp) => `${tp.name}|${tp.language}` === test.template);
  const sendTest = () =>
    chosen &&
    void run(
      () => api.testWhatsApp({ to: test.to.trim(), template: chosen.name, language: chosen.language, params: test.params.split('|').map((p) => p.trim()).filter(Boolean) }),
      t('wa.test.sent'),
    );

  return (
    <div className="section-gap">
      <h2 className="section-title">{t('wa.title')}</h2>
      <p className="hint">{t('wa.hint')}</p>
      {msg && <div className={`banner ${msg.kind} small`}>{msg.text}</div>}
      {view && !configured && !editing && (
        <div className="card line compact-line">
          <div className="row-between">
            <span className="muted">{hosted ? t('wa.host.none') : t('wa.none')}</span>
            {hosted && account?.connectUrl
              ? <a className="primary" href={account.connectUrl} target="_blank" rel="noopener noreferrer">{t('wa.host.connect')}</a>
              : <button type="button" className="primary" onClick={startEdit}>{t('wa.connect')}</button>}
          </div>
          {hosted && account?.hostError && <div className="hint">{t(`error.${account.hostError}`) === `error.${account.hostError}` ? account.hostError : t(`error.${account.hostError}`)}</div>}
        </div>
      )}
      {editing && (
        <div className="card line-form">
          <h3>{t('wa.title')}</h3>
          <p className="hint">{t('wa.where')}</p>
          <Field label={t('wa.phoneNumberId')} hint={t('wa.phoneNumberId.hint')}>
            <input value={x.phoneNumberId} inputMode="numeric" autoComplete="off" onChange={(e) => setX({ ...x, phoneNumberId: e.target.value })} />
          </Field>
          <Field label={t('wa.wabaId')} hint={t('wa.wabaId.hint')}>
            <input value={x.wabaId} inputMode="numeric" autoComplete="off" onChange={(e) => setX({ ...x, wabaId: e.target.value })} />
          </Field>
          <Field label={t('wa.token')} hint={configured ? t('lines.password.keep') : t('wa.token.hint')}>
            <input type="password" value={x.token} autoComplete="new-password" onChange={(e) => setX({ ...x, token: e.target.value })} />
          </Field>
          <Field label={t('wa.language')} hint={t('wa.language.hint')}>
            <input value={x.defaultLanguage} placeholder="tr" maxLength={5} onChange={(e) => setX({ ...x, defaultLanguage: e.target.value })} />
          </Field>
          <p className="hint">{t('lines.secretNote')}</p>
          <div className="row-between section-gap">
            <span />
            <span className="row">
              <button type="button" className="ghost" onClick={() => setEditing(false)}>{t('settings.discard')}</button>
              <button type="button" className="primary" disabled={busy || !x.phoneNumberId.trim() || !x.wabaId.trim() || (!configured && !x.token.trim())} onClick={save}>
                {busy ? t('sms.checking') : t('sms.save')}
              </button>
            </span>
          </div>
        </div>
      )}
      {configured && !editing && (
        <>
          <div className="card line compact-line">
            <div className="row-between">
              <div>
                <div className="row">
                  <strong>{account.name || 'WhatsApp'}</strong>
                  <span className={`pill ${account.verified ? 'ok' : 'warn'}`}>{account.verified ? t('sms.verified') : t('sms.unverified')}</span>
                </div>
                <div className="hint mono">{account.managedBy === 'host' ? account.phone : (account.phone ?? account.phoneNumberId)}</div>
                {account.managedBy === 'host' && <div className="hint">{t('wa.host.managed')}</div>}
                <div className="hint">
                  {account.templates === null
                    ? t('wa.templates.unknown')
                    : t('wa.templates.count', { n: approved.length, all: account.templates.length })}
                </div>
              </div>
              {account.managedBy === 'host' ? (
                <span className="row">
                  <a className="ghost" href={account.connectUrl} target="_blank" rel="noopener noreferrer">{t('wa.host.manage')}</a>
                </span>
              ) : (
                <span className="row">
                  <button type="button" className="ghost" onClick={startEdit}>{t('lines.edit')}</button>
                  <DangerButton t={t} label={t('settings.remove')} onConfirm={() => void run(() => api.removeWhatsApp(), t('wa.removed'))} />
                </span>
              )}
            </div>
          </div>
          {account.templates && account.templates.length > 0 && (
            <table className="table">
              <thead><tr><th>{t('wa.template')}</th><th>{t('wa.language')}</th><th>{t('wa.category')}</th><th>{t('sms.log.status')}</th></tr></thead>
              <tbody>
                {account.templates.map((tp) => (
                  <tr key={`${tp.name}|${tp.language}`}>
                    <td className="mono">{tp.name}</td>
                    <td>{tp.language}</td>
                    <td>{tp.category}</td>
                    <td><span className={`pill ${tp.status === 'APPROVED' ? 'ok' : tp.status === 'REJECTED' ? 'error' : 'warn'}`}>{tp.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="card line-form">
            <h3>{t('wa.test')}</h3>
            <Field label={t('sms.test.to')} hint={t('wa.test.to.hint')}>
              <input value={test.to} inputMode="tel" placeholder="+90…" onChange={(e) => setTest({ ...test, to: e.target.value })} />
            </Field>
            <Field label={t('wa.template')} {...(approved.length ? {} : { hint: t('wa.template.none') })}>
              <select value={test.template} onChange={(e) => setTest({ ...test, template: e.target.value })}>
                <option value="">—</option>
                {approved.map((tp) => <option key={`${tp.name}|${tp.language}`} value={`${tp.name}|${tp.language}`}>{tp.name} ({tp.language})</option>)}
              </select>
            </Field>
            <Field label={t('wa.params')} hint={t('wa.params.hint')}>
              <input value={test.params} onChange={(e) => setTest({ ...test, params: e.target.value })} />
            </Field>
            <div className="row-between">
              <span className="hint">{t('sms.limits')}</span>
              <button type="button" className="primary" disabled={busy || !test.to.trim() || !chosen} onClick={sendTest}>{t('sms.test.send')}</button>
            </div>
          </div>
          {view && view.messages.length > 0 && (
            <table className="table">
              <thead><tr><th>{t('sms.log.when')}</th><th>{t('sms.log.to')}</th><th>{t('sms.log.kind')}</th><th>{t('sms.log.status')}</th><th>{t('sms.log.text')}</th></tr></thead>
              <tbody>
                {view.messages.map((m) => (
                  <tr key={m.id}>
                    <td className="nowrap">{new Date(m.createdAt).toLocaleString()}</td>
                    <td className="mono">{m.to}</td>
                    <td>{t(`sms.kind.${m.kind}`) === `sms.kind.${m.kind}` ? m.kind : t(`sms.kind.${m.kind}`)}</td>
                    <td>
                      <span className={`pill ${m.status === 'sent' ? 'ok' : 'error'}`}>{t(`sms.status.${m.status}`)}</span>
                      {m.error && <div className="hint">{t(`error.${m.error}`) === `error.${m.error}` ? m.error : t(`error.${m.error}`)}</div>}
                    </td>
                    <td className="hint">{m.body}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};
